import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { I18nProvider } from "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import api from "@/lib/api";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieConsent from "@/components/CookieConsent";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Course from "@/pages/Course";
import Lesson from "@/pages/Lesson";
import Search from "@/pages/Search";
import Bookmarks from "@/pages/Bookmarks";
import Notes from "@/pages/Notes";
import Certificate from "@/pages/Certificate";
import Profile from "@/pages/Profile";
import Legal from "@/pages/Legal";
import Admin from "@/pages/Admin";
import Curriculum from "@/pages/Curriculum";
import PublicLesson from "@/pages/PublicLesson";
import { captureFirstTouch } from "@/lib/attribution";

function VisitTracker() {
  React.useEffect(() => {
    const cur = captureFirstTouch();
    if (sessionStorage.getItem("visit_tracked")) return;
    sessionStorage.setItem("visit_tracked", "1");
    api.post("/track/visit", { source: cur.source }).catch(() => {});
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent Google OAuth callback FIRST (synchronously during render).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/legal/:page" element={<Legal />} />
      <Route path="/curriculum" element={<Curriculum />} />
      <Route path="/learn/:lessonId" element={<PublicLesson />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/course" element={<ProtectedRoute><Course /></ProtectedRoute>} />
      <Route path="/lesson/:lessonId" element={<ProtectedRoute><Lesson /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
      <Route path="/bookmarks" element={<ProtectedRoute><Bookmarks /></ProtectedRoute>} />
      <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
      <Route path="/certificate" element={<ProtectedRoute><Certificate /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="App min-h-screen bg-void grain">
            <VisitTracker />
            <AppRouter />
            <CookieConsent />
            <Toaster theme="dark" position="bottom-right" richColors />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}
